import React, { useState } from 'react'

import { Context } from 'types/GlobalUserEvents'
import { TResource } from 'types/GlobalResource'

import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useAvailableDashboardGroupsIncludeDashboards } from 'redux/hooks/DashboardGroups'
import { useKeyMetric } from 'redux/hooks/KeyMetrics'
import { useKeyMetricActions } from 'redux/actions/KeyMetrics'
import { useReports } from 'redux/hooks/Reports'
import { useParams, useResources } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import SectionHeader from 'components_new/molecules/SectionHeader'

import ResourceTable from 'components_new/organisms/ResourcesTable'
import UnlinkDialog from 'components_new/organisms/dialogs/UnlinkDialog'

import KeyMetricTemplate from 'components_new/templates/KeyMetricTemplate'

import Empty from './empty'
import { getResourceVariant } from 'types/GlobalKeyMetric'

const KeyMetricResources = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const keyMetric = useKeyMetric(id)
  const { tryUpdateKeyMetric } = useKeyMetricActions()

  // const isOwner = useIsOwner(id)
  const { isAdmin } = useAccountSettings()

  const [dialog, setDialog] = useState<{
    data: { id: string; title: string } | null
    open: boolean
    variant: 'unlink'
  }>({ data: null, open: false, variant: 'unlink' })

  const handleUnlink = (resource: TResource) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { id: resource.id, title: resource.title },
      open: true,
      variant: 'unlink'
    }))
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({
      ...prevState,
      open: false
    }))
  }

  const allDashboardGroups = useAvailableDashboardGroupsIncludeDashboards()
  const allReports = useReports()
  const resourceIds = new Set(keyMetric?.resources.map((r) => r.id) ?? [])
  const dashboardGroups = allDashboardGroups.filter((g) =>
    resourceIds.has(g.id)
  )
  const reports = allReports.filter((r) => resourceIds.has(r.id))
  const resources = useResources({ dashboardGroups, reports })

  return (
    <>
      <KeyMetricTemplate
        subpage={{
          icon: { name: 'Resources' },
          title: t('global', 'resources')
        }}
        variant="static"
      >
        <SectionHeader
          gutterBottom={true}
          size="large"
          title={t('global', 'resources')}
        />
        {dashboardGroups.length === 0 ? (
          <Empty />
        ) : (
          <ResourceTable
            context={Context.FROM_KEY_METRICS_RESOURCES}
            editMode={isAdmin}
            items={resources}
            onClickItemUnlink={(_, resource) => handleUnlink(resource)}
          />
        )}
      </KeyMetricTemplate>

      {/*-- dialogs --*/}

      <UnlinkDialog
        open={dialog.open && dialog.variant === 'unlink'}
        onClose={handleCloseDialog}
        onUnlink={(done) => {
          if (keyMetric && dialog.data) {
            const resources = keyMetric.resources
              .filter((r) => r.id !== dialog.data?.id)
              .map((r) => ({ id: r.id, type: getResourceVariant(r) }))

            tryUpdateKeyMetric(keyMetric.id, { resources })
          }

          handleCloseDialog()
          done()
        }}
        title={dialog.data?.title ?? ''}
        source={keyMetric?.title ?? ''}
        type={t('global', 'resource')}
      />
    </>
  )
}

export default KeyMetricResources
